import type { KeyName } from '@/lib/key';

import type { Keys } from 'react-hotkeys-hook';

type ShortcutDefinition = {
  label: string;
  defaultCombo: Keys;
  keys: readonly KeyName[];
  keysEnd?: readonly KeyName[];
};

export const SHORTCUT_DEFINITIONS = {
  'app.newChat': {
    label: 'New chat',
    defaultCombo: 'mod+n',
    keys: ['mod', 'n'],
  },
  'app.commandBar': {
    label: 'Search',
    defaultCombo: '$mod+k',
    keys: ['mod', 'k'],
  },
  'app.openSettings': {
    label: 'Open settings',
    defaultCombo: 'mod+comma',
    keys: ['mod', 'comma'],
  },
  'app.toggleSidebar': {
    label: 'Toggle sidebar',
    defaultCombo: 'mod+s',
    keys: ['mod', 's'],
  },
  'navigation.back': {
    label: 'Go back',
    defaultCombo: 'mod+[',
    keys: ['mod', 'bracketleft'],
  },
  'navigation.forward': {
    label: 'Go forward',
    defaultCombo: 'mod+]',
    keys: ['mod', 'bracketright'],
  },
  'tabs.new': {
    label: 'New tab',
    defaultCombo: 'mod+t',
    keys: ['mod', 't'],
  },
  'tabs.close': {
    label: 'Close tab',
    defaultCombo: 'mod+w',
    keys: ['mod', 'w'],
  },
  'tabs.reopen': {
    label: 'Reopen last closed tab',
    defaultCombo: 'mod+shift+t',
    keys: ['mod', 'shift', 't'],
  },
  'tabs.previous': {
    label: 'Previous tab',
    defaultCombo: 'mod+shift+[',
    keys: ['mod', 'shift', 'bracketleft'],
  },
  'tabs.next': {
    label: 'Next tab',
    defaultCombo: 'mod+shift+]',
    keys: ['mod', 'shift', 'bracketright'],
  },
  'tabs.switch': {
    label: 'Switch to tab',
    defaultCombo: Array.from({ length: 9 }, (_, i) => `mod+${i + 1}`),
    keys: ['mod', '1'],
    keysEnd: ['mod', '9'],
  },
  'chat.send': {
    label: 'Send message',
    defaultCombo: 'enter,mod+enter',
    keys: ['enter'],
  },
  'chat.modeDefault': {
    label: 'Switch to Default',
    defaultCombo: 'ctrl+d',
    keys: ['control', 'd'],
  },
  'chat.modeQuick': {
    label: 'Toggle Quick',
    defaultCombo: 'ctrl+q',
    keys: ['control', 'q'],
  },
  'chat.modeMax': {
    label: 'Toggle Max',
    defaultCombo: 'ctrl+m',
    keys: ['control', 'm'],
  },
  'chat.modeUltra': {
    label: 'Toggle Ultra',
    defaultCombo: 'ctrl+u',
    keys: ['control', 'u'],
  },
  'chat.voiceInput': {
    label: 'Start voice recording (press again to stop)',
    defaultCombo: 'ctrl+shift+d',
    keys: ['control', 'shift', 'd'],
  },
  'chat.cancelVoiceInput': {
    label: 'Cancel voice recording',
    defaultCombo: 'alt+esc',
    keys: ['option', 'escape'],
  },
  'chat.find': {
    label: 'Find in chat',
    defaultCombo: 'mod+f',
    keys: ['mod', 'f'],
  },
  'chat.attachFile': {
    label: 'Attach file',
    defaultCombo: 'mod+u',
    keys: ['mod', 'u'],
  },
  'meetings.toggle': {
    label: 'Start/stop meeting note',
    defaultCombo: 'mod+l',
    keys: ['mod', 'l'],
  },
  'contextChat.toggle': {
    label: 'Open context chat',
    defaultCombo: 'mod+j',
    keys: ['mod', 'j'],
  },
  'terminal.toggle': {
    label: 'Toggle terminal',
    defaultCombo: 'ctrl+backquote',
    keys: ['control', 'backquote'],
  },
  'hummingbird.newChat': {
    label: 'New chat',
    defaultCombo: 'mod+n',
    keys: ['mod', 'n'],
  },
  'hummingbird.actionsMenu': {
    label: 'Open actions menu',
    defaultCombo: 'mod+k',
    keys: ['mod', 'k'],
  },
  'hummingbird.captureScreenshot': {
    label: 'Capture screenshot',
    defaultCombo: 'mod+shift+c',
    keys: ['mod', 'shift', 'c'],
  },
  'hummingbird.toggleMeeting': {
    label: 'Toggle ongoing meeting transcript inclusion',
    defaultCombo: 'mod+shift+m',
    keys: ['mod', 'shift', 'm'],
  },
  'hummingbird.toggleApp': {
    label: 'Toggle app context inclusion',
    defaultCombo: 'mod+shift+a',
    keys: ['mod', 'shift', 'a'],
  },
  'hummingbird.toggleTextSelection': {
    label: 'Toggle text selection inclusion',
    defaultCombo: 'mod+shift+s',
    keys: ['mod', 'shift', 's'],
  },
  'hummingbird.copyResponse': {
    label: 'Copy last response',
    defaultCombo: 'ctrl+c',
    keys: ['control', 'c'],
  },
  'hummingbird.pasteResponse': {
    label: 'Paste last response',
    defaultCombo: 'ctrl+v',
    keys: ['control', 'v'],
  },
  'hummingbird.pasteResponsePlain': {
    label: 'Paste last response as plain text',
    defaultCombo: 'ctrl+shift+v',
    keys: ['control', 'shift', 'v'],
  },
  'hummingbird.expandToMain': {
    label: 'Expand chat to main window',
    defaultCombo: 'ctrl+e',
    keys: ['control', 'e'],
  },
  'typeScale.reset': {
    label: 'Reset to normal',
    defaultCombo: 'ctrl+=',
    keys: ['control', 'equal'],
  },
  'typeScale.increase': {
    label: 'Increase size',
    defaultCombo: 'ctrl+shift+=',
    keys: ['control', 'shift', 'equal'],
  },
  'typeScale.decrease': {
    label: 'Decrease size',
    defaultCombo: 'ctrl+shift+minus',
    keys: ['control', 'shift', 'minus'],
  },
} satisfies Record<string, ShortcutDefinition>;

export type ShortcutId = keyof typeof SHORTCUT_DEFINITIONS;

export function getShortcutDefinition(id: ShortcutId): ShortcutDefinition {
  return SHORTCUT_DEFINITIONS[id];
}
